// /resources/js/app/modules/v2/OptimisticSignalBuilder.js
import axios from 'axios';
import { apiClient } from './apiClient.js';

/**
 * @module OptimisticSignalBuilder
 * Creates a rich, "optimistic" signal object for immediate UI display
 * by fetching live token data and historical source data in parallel.
 */
class OptimisticSignalBuilder {
    async _fetchTokenData(chain, tokenAddress) {
        try {
            // --- STEP 1: TOKEN-FIRST ATTEMPT (No change here) ---
            const tokenApiUrl = `https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`;
            const tokenResponse = await axios.get(tokenApiUrl);
            const tokenPairData = tokenResponse.data.pairs?.[0];

            if (tokenPairData) {
                // If we found it as a token, return the data immediately.
                return tokenPairData;
            }

            // --- STEP 2: POOL-FALLBACK ATTEMPT (The new logic) ---
            console.log(`[OptimisticSignalBuilder] Token lookup failed for ${tokenAddress}. Retrying as a pool address.`);
            const pairApiUrl = `https://api.dexscreener.com/latest/dex/pairs/${chain}/${tokenAddress}`;
            const pairResponse = await axios.get(pairApiUrl);
            
            // The API response for a pair is nested inside a "pairs" array.
            return pairResponse.data.pairs?.[0] || null;

        } catch (error) {
            console.warn(`[OptimisticSignalBuilder] Both token and pair lookups failed for ${tokenAddress}:`, error.message);
            return null; // Return null if both attempts fail.
        }
    }

    async _fetchSourceData(sourceId) {
        try {
            const response = await apiClient.get(`/api/sources/${sourceId}/snapshot`);
            return response.data;
        } catch (error) {
            console.warn(`[OptimisticSignalBuilder] Source snapshot fetch failed for ${sourceId}:`, error.message);
            return null;
        }
    }
    async create(rawCallData, tokenData) {
        if (!tokenData) return null; // Guard clause if no token data is provided

        const { sourceId, sourceName, timestamp } = rawCallData;
        
        // The only API call left in this builder is to get the source's historical profile
        const sourceSnapshot = await this._fetchSourceData(sourceId);

        // Extract corrected data from the pre-fetched tokenData object
        const finalChain = tokenData.chainId;
        const finalTokenAddress = tokenData.baseToken.address;

        return {
            isOptimistic: true,
            call_id: `optimistic-${Date.now()}`,
            call_timestamp: new Date(timestamp * 1000).toISOString(),
            asset: {
                id: null,
                chain_id: finalChain,
                token_address: finalTokenAddress,
                pool_address: tokenData.pairAddress,
                symbol: tokenData.baseToken?.symbol,
                name: tokenData.baseToken?.name,
                metadata_json: { imageUrl: tokenData.info?.imageUrl }
            },
            asset_symbol: tokenData.baseToken?.symbol || '...',
            asset_name: tokenData.baseToken?.name || 'Processing...',
            token_address: finalTokenAddress, 
            source: { profile: sourceSnapshot },
            source_id: sourceId,
            source_name: sourceName, 
            source_tier: sourceSnapshot?.tier || 'bronze',
            source_tier_rank: sourceSnapshot?.tier_rank || null,
            source_profile: sourceSnapshot,
            network_consensus: 1,
            max_roi: null,
            live_data: { market_data: tokenData, live_pnl: 0 },
            sparkline_1h: [],
            lock_status: 'unlocked',
            unlock_cost: null,
            teaser_data: null,
        };
    }
}

export const optimisticSignalBuilder = new OptimisticSignalBuilder();